import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, FileDown, FileText, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_ROWS = 25;

export default function BulkUploadModal({ onClose, onImported }) {
  const [stage, setStage] = useState("drop"); // "drop" | "preview" | "importing"
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, stats }
  const [includeDuplicates, setIncludeDuplicates] = useState(false);
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const validateFile = (f) => {
    setError("");
    if (!f) return false;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setError("Only .csv files are supported.");
      return false;
    }
    if (f.size > MAX_BYTES) {
      setError("File too large. Max 5 MB.");
      return false;
    }
    return true;
  };

  const onPickFile = (f) => {
    if (!validateFile(f)) return;
    setFile(f);
    runPreview(f);
  };

  const runPreview = async (f) => {
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/tenders/bulk-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setStage("preview");
    } catch (ex) {
      setError(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    onPickFile(f);
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/tenders/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tender_upload_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const importNow = async () => {
    if (!preview) return;
    const rows = preview.rows
      .filter((r) => r.errors.length === 0)
      .filter((r) => includeDuplicates || !r.duplicate)
      .map((r) => ({
        client: r.client,
        location: r.location,
        title: r.title,
        gem_type: r.gem_type,
        deadline: r.deadline,
        tender_type: r.tender_type,
        tender_type_other: r.tender_type_other || "",
        supply_service: r.supply_service,
        contract_value: r.contract_value,
        remarks: r.remarks || "",
      }));
    if (rows.length === 0) {
      setError("No valid rows to import.");
      return;
    }
    setStage("importing");
    setLoading(true);
    try {
      const { data } = await api.post("/tenders/bulk-import", { rows });
      toast.success(`${data.imported_count} tenders imported successfully.`);
      onImported(data.inserted.map((t) => t.id));
      onClose();
    } catch (ex) {
      setError(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      setStage("preview");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setStage("drop"); setError("");
  };

  return (
    createPortal(<div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-upload-modal">
      <div className="bg-white max-w-5xl w-full border border-ink-line rounded-sm max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-brand" />
            <div className="font-display font-bold tracking-tight">Upload Tender Sheet</div>
            <span className="text-[11px] uppercase tracking-wider text-ink-muted font-num">CSV · max 25 rows · 5 MB</span>
          </div>
          <button onClick={onClose} className="p-1 text-ink-muted hover:text-ink" data-testid="bulk-close"><X className="w-4 h-4" /></button>
        </div>

        {/* Drop stage */}
        {stage === "drop" && (
          <div className="p-8 overflow-y-auto" data-testid="bulk-drop-stage">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="font-display font-bold text-lg tracking-tight">Step 1 · Choose a CSV</div>
                <p className="text-sm text-ink-muted mt-1">Use our template to make sure the headers match exactly.</p>
              </div>
              <button onClick={downloadTemplate} data-testid="bulk-download-template" className="flex items-center gap-2 px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary transition-colors">
                <FileDown className="w-4 h-4" /> Download template
              </button>
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              data-testid="bulk-dropzone"
              className={`border-2 border-dashed rounded-sm py-16 px-6 text-center cursor-pointer transition-all ${
                dragOver ? "border-brand bg-brand-soft" : "border-ink-line hover:border-brand hover:bg-secondary/50"
              }`}
            >
              <Upload className={`w-10 h-10 mx-auto mb-4 ${dragOver ? "text-brand" : "text-ink-muted"}`} />
              <div className="font-display font-semibold text-lg tracking-tight">Drop CSV here</div>
              <p className="text-sm text-ink-muted mt-1">or click to browse from your computer</p>
              <p className="text-[11px] uppercase tracking-wider text-ink-muted mt-6">Up to 25 tenders · 5 MB · .csv only</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => onPickFile(e.target.files?.[0])}
                data-testid="bulk-file-input"
              />
            </div>

            {error && (
              <div className="mt-4 text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm flex items-start gap-2" data-testid="bulk-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}

            {loading && <div className="mt-4 text-sm text-ink-muted uppercase tracking-wider">Validating…</div>}

            <div className="mt-8 text-xs text-ink-muted leading-relaxed border-t border-ink-line pt-5">
              <div className="font-display font-semibold tracking-tight text-sm text-ink mb-2">Required headers (case-insensitive)</div>
              <div className="font-num text-[12px]">
                Client · Location · Tender Title · GeM/Non-GeM · Closing Date · Type · Supply/Service · Contract Value · Remarks <span className="text-ink-muted">(optional)</span>
              </div>
              <div className="mt-2">Closing Date is parsed leniently (e.g., <code className="font-num bg-secondary px-1 rounded-sm">05-Feb-26</code>, <code className="font-num bg-secondary px-1 rounded-sm">2026-02-05</code>, <code className="font-num bg-secondary px-1 rounded-sm">5/2/2026</code>). Past dates are allowed and shown as <strong>Closed</strong>.</div>
              <div className="mt-1">Header row may appear in any of the first 10 rows — column names are matched fuzzily (e.g., "Customer" → Client).</div>
            </div>
          </div>
        )}

        {/* Preview stage */}
        {stage === "preview" && preview && (
          <div className="flex flex-col overflow-hidden" data-testid="bulk-preview-stage">
            <div className="px-6 py-4 border-b border-ink-line flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="chip border-good/30 bg-good-soft text-good" data-testid="bulk-valid-count">
                  <CheckCircle2 className="w-3 h-3" /> {preview.stats.valid} valid
                </div>
                {preview.stats.invalid > 0 && (
                  <div className="chip border-warn/30 bg-warn-soft text-warn" data-testid="bulk-invalid-count">
                    <AlertTriangle className="w-3 h-3" /> {preview.stats.invalid} with errors
                  </div>
                )}
                {preview.stats.duplicates > 0 && (
                  <div className="chip border-ink-line bg-secondary text-ink" data-testid="bulk-dup-count">
                    {preview.stats.duplicates} possible duplicates
                  </div>
                )}
                <div className="text-[11px] uppercase tracking-wider text-ink-muted font-num">
                  of {preview.stats.total} rows
                </div>
                {file && <div className="text-xs text-ink-muted flex items-center gap-1.5"><FileText className="w-3 h-3" /> {file.name}</div>}
              </div>
              {preview.stats.duplicates > 0 && (
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={includeDuplicates} onChange={(e) => setIncludeDuplicates(e.target.checked)} className="accent-brand" data-testid="bulk-include-dups" />
                  Import possible duplicates too
                </label>
              )}
            </div>

            {preview.unrecognised_headers && preview.unrecognised_headers.length > 0 && (
              <div className="mx-6 mt-3 text-xs bg-warn-soft border border-warn/30 text-ink px-3 py-2 rounded-sm flex items-start gap-2" data-testid="bulk-unrecognised">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-warn" />
                <div>
                  <div className="font-medium text-warn">Unrecognised columns (will be ignored)</div>
                  <div className="text-ink-muted mt-0.5 font-num">{preview.unrecognised_headers.join(" · ")}</div>
                  {preview.header_row_number && preview.header_row_number > 1 && (
                    <div className="text-ink-muted mt-0.5">Header detected on row {preview.header_row_number}.</div>
                  )}
                </div>
              </div>
            )}

            <div className="overflow-auto flex-1 nice-scroll">
              <table className="w-full text-xs">
                <thead className="bg-secondary border-b border-ink-line sticky top-0">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted">
                    <th className="px-3 py-2.5 font-medium">Row</th>
                    <th className="px-3 py-2.5 font-medium">Client</th>
                    <th className="px-3 py-2.5 font-medium">Location</th>
                    <th className="px-3 py-2.5 font-medium">Title</th>
                    <th className="px-3 py-2.5 font-medium">GeM</th>
                    <th className="px-3 py-2.5 font-medium">Closing</th>
                    <th className="px-3 py-2.5 font-medium">State</th>
                    <th className="px-3 py-2.5 font-medium">Type</th>
                    <th className="px-3 py-2.5 font-medium">S/S</th>
                    <th className="px-3 py-2.5 font-medium">Value</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const hasErrors = r.errors.length > 0;
                    const skipDup = r.duplicate && !includeDuplicates;
                    const rowCls = hasErrors
                      ? "bg-warn-soft/50 border-warn/20"
                      : (skipDup ? "bg-secondary/40 opacity-70" : "");
                    return (
                      <tr key={r.row_number} className={`border-b border-ink-line last:border-b-0 ${rowCls}`} data-testid={`bulk-row-${r.row_number}`}>
                        <td className="px-3 py-2 font-num text-ink-muted">{r.row_number}</td>
                        <td className="px-3 py-2 font-medium max-w-[140px] truncate" title={r.client}>{r.client || "—"}</td>
                        <td className="px-3 py-2 text-ink-muted">{r.location || "—"}</td>
                        <td className="px-3 py-2 max-w-[180px] truncate" title={r.title}>{r.title || "—"}</td>
                        <td className="px-3 py-2 text-ink-muted">{r.gem_type || "—"}</td>
                        <td className="px-3 py-2 font-num">{r.deadline || "—"}</td>
                        <td className="px-3 py-2">
                          {r.open_closed === "Open" ? (
                            <span className="chip border-good/40 bg-good-soft text-good text-[10px]">Open</span>
                          ) : r.open_closed === "Closed" ? (
                            <span className="chip border-ink-line bg-secondary text-ink-muted text-[10px]">Closed</span>
                          ) : (
                            <span className="text-ink-muted text-[10px]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-ink-muted">{r.tender_type || "—"}</td>
                        <td className="px-3 py-2 text-ink-muted">{r.supply_service || "—"}</td>
                        <td className="px-3 py-2 font-num">{r.contract_value || "—"}</td>
                        <td className="px-3 py-2">
                          {hasErrors ? (
                            <span className="text-warn flex items-start gap-1 group relative cursor-help">
                              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                              <span className="text-[11px]">{r.errors.length} issue{r.errors.length > 1 ? "s" : ""}</span>
                              <span className="absolute left-0 top-full mt-1 hidden group-hover:block bg-ink text-white text-[11px] px-2 py-1 rounded-sm whitespace-pre z-10 max-w-xs">
                                {r.errors.join("\n")}
                              </span>
                            </span>
                          ) : r.duplicate ? (
                            <span className="text-ink-muted text-[11px]">Possible duplicate</span>
                          ) : (
                            <span className="text-good text-[11px] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Ready</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {error && (
              <div className="mx-6 mb-2 text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm">{error}</div>
            )}

            <div className="px-6 py-4 border-t border-ink-line flex items-center justify-between flex-wrap gap-3">
              <button onClick={reset} data-testid="bulk-reset" className="text-sm text-ink-muted hover:text-ink underline-offset-4 hover:underline">
                ← Fix & re-upload
              </button>
              <div className="flex items-center gap-3">
                <button onClick={onClose} className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary transition-colors" data-testid="bulk-cancel">
                  Cancel
                </button>
                <button
                  onClick={importNow}
                  disabled={loading || preview.stats.valid === 0}
                  data-testid="bulk-import-btn"
                  className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors"
                >
                  {loading ? "Importing…" : `Import ${countToImport(preview, includeDuplicates)} ${countToImport(preview, includeDuplicates) === 1 ? "row" : "rows"}`}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>, document.body)
  );
}

function countToImport(preview, includeDuplicates) {
  if (!preview) return 0;
  return preview.rows.filter((r) => r.errors.length === 0 && (includeDuplicates || !r.duplicate)).length;
}
